package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppColors
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminEpisodeDetailDto
import com.castly.shared.dto.UpdateAudiobookEpisodeRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

/**
 * EpisodeEditPanel — full edit panel for a single audiobook episode.
 * Shows metadata fields, image upload, audio replacement, translations, and delete/restore controls.
 */
@Composable
fun EpisodeEditPanel(
    episode: AdminEpisodeDetailDto,
    apiClient: ApiClient,
    isAdmin: Boolean,
    onClose: () -> Unit,
    onEpisodeUpdated: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    // Metadata form state
    var title by remember(episode.id) { mutableStateOf(episode.title) }
    var description by remember(episode.id) { mutableStateOf(episode.description ?: "") }
    var episodeNumber by remember(episode.id) { mutableStateOf(episode.episodeNumber?.toString() ?: "") }
    var durationSeconds by remember(episode.id) { mutableStateOf(episode.durationSeconds?.toString() ?: "") }

    // Save state
    var isSavingMeta by remember { mutableStateOf(false) }
    var metaError by remember { mutableStateOf<String?>(null) }
    var metaSuccess by remember { mutableStateOf(false) }

    // Image upload state
    var currentImageUrl by remember(episode.id) { mutableStateOf(episode.imageUrl) }
    var isUploadingImage by remember { mutableStateOf(false) }
    var imageError by remember { mutableStateOf<String?>(null) }

    // Audio replacement state
    var isReplacingAudio by remember { mutableStateOf(false) }
    var audioError by remember { mutableStateOf<String?>(null) }
    var audioSuccess by remember { mutableStateOf(false) }
    var showAudioConfirm by remember { mutableStateOf(false) }
    var pendingAudioFile by remember { mutableStateOf<File?>(null) }

    // Delete/restore state
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var isDeleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }
    var isRestoring by remember { mutableStateOf(false) }

    val isDeleted = episode.deletedAt != null

    LaunchedEffect(metaSuccess) {
        if (metaSuccess) { kotlinx.coroutines.delay(2000); metaSuccess = false }
    }
    LaunchedEffect(audioSuccess) {
        if (audioSuccess) { kotlinx.coroutines.delay(2000); audioSuccess = false }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
            padding(Spacing.lg)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            H3({
                style {
                    fontSize(Typography.xl)
                    fontWeight("700")
                    color(Color(colors.text))
                    margin(0.px)
                }
            }) {
                Text("Edit Chapter ${episode.episodeNumber?.let { "#$it" } ?: ""}")
            }
            Button({
                onClick { onClose() }
                style {
                    fontSize(Typography.xl)
                    background("none")
                    border(0.px)
                    cursor("pointer")
                    color(Color(colors.textMuted))
                }
            }) { Text("×") }
        }

        if (isDeleted) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fef9c3" else "#422006"))
                    border(1.px, LineStyle.Solid, Color("#d97706"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.md)
                    fontSize(Typography.sm)
                    color(Color(if (colors == LightColors) "#92400e" else "#fde68a"))
                }
            }) {
                Text("This chapter is deleted. Deleted on: ${episode.deletedAt}")
            }
        }

        // ── Metadata Section ────────────────────────────────────────────────

        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.md)
                themeTransition()
            }
        }) {
            H4({ style { margin(0.px); fontSize(Typography.base); fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Metadata")
            }

            FormField("Title") {
                Input(type = InputType.Text) {
                    value(title)
                    onInput { title = it.value }
                    placeholder("Chapter title")
                    inputStyle(colors)
                }
            }
            FormField("Description") {
                TextArea(attrs = {
                    value(description)
                    onInput { description = it.value }
                    placeholder("Chapter description")
                    style {
                        width(100.percent)
                        height(80.px)
                        padding(Spacing.sm, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                        property("resize", "vertical")
                        themeTransition()
                    }
                })
            }
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.md) } }) {
                Div({ style { flex("1") } }) {
                    FormField("Chapter Number") {
                        Input(type = InputType.Number) {
                            value(episodeNumber)
                            onInput { episodeNumber = it.value?.toString() ?: "" }
                            placeholder("e.g. 1")
                            inputStyle(colors)
                        }
                    }
                }
                Div({ style { flex("1") } }) {
                    FormField("Duration (seconds)") {
                        Input(type = InputType.Number) {
                            value(durationSeconds)
                            onInput { durationSeconds = it.value?.toString() ?: "" }
                            placeholder("e.g. 1800")
                            inputStyle(colors)
                        }
                    }
                }
            }

            metaError?.let { err ->
                ErrorBanner(err, colors)
            }
            if (metaSuccess) SuccessBanner("Changes saved", colors)

            Button({
                onClick {
                    scope.launch {
                        isSavingMeta = true
                        metaError = null
                        try {
                            apiClient.updateAudiobookEpisodeMetadata(
                                audiobookId = episode.audiobookIsbn,
                                episodeId = episode.id,
                                request = UpdateAudiobookEpisodeRequest(
                                    title = title.takeIf { it.isNotBlank() },
                                    description = description.takeIf { it.isNotBlank() },
                                    episodeNumber = episodeNumber.toIntOrNull(),
                                    durationSeconds = durationSeconds.toIntOrNull()
                                )
                            )
                            metaSuccess = true
                            onEpisodeUpdated()
                        } catch (e: Exception) {
                            metaError = if (e.message?.contains("EPISODE_NUMBER_CONFLICT") == true)
                                "Chapter number already exists for this audiobook"
                            else e.message ?: "Failed to save changes"
                        } finally {
                            isSavingMeta = false
                        }
                    }
                }
                if (isSavingMeta) disabled()
                primaryButtonStyle(colors, isSavingMeta)
            }) { Text(if (isSavingMeta) "Saving..." else "Save Metadata") }
        }

        // ── Image Section ──────────────────────────────────────────────────

        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.md)
                themeTransition()
            }
        }) {
            H4({ style { margin(0.px); fontSize(Typography.base); fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Thumbnail Image")
            }

            currentImageUrl?.let { url ->
                Img(src = url, alt = "Episode thumbnail") {
                    style {
                        width(120.px)
                        height(120.px)
                        property("object-fit", "cover")
                        borderRadius(BorderRadius.md)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                    }
                }
            }

            imageError?.let { ErrorBanner(it, colors) }

            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); alignItems(AlignItems.Center) } }) {
                Input(type = InputType.File) {
                    attr("id", "episode-image-upload-${episode.id}")
                    attr("accept", ".jpg,.jpeg,.png")
                    style { property("display", "none") }
                    onChange { event ->
                        val file = (event.target as? HTMLInputElement)?.files?.item(0)
                        if (file != null) {
                            scope.launch {
                                isUploadingImage = true
                                imageError = null
                                try {
                                    val result = apiClient.uploadAudiobookEpisodeImage(episode.audiobookIsbn, episode.id, file)
                                    currentImageUrl = result["imageUrl"]
                                    onEpisodeUpdated()
                                } catch (e: Exception) {
                                    imageError = e.message ?: "Upload failed"
                                } finally {
                                    isUploadingImage = false
                                }
                            }
                        }
                    }
                }
                Button({
                    onClick { kotlinx.browser.document.getElementById("episode-image-upload-${episode.id}")?.asDynamic()?.click() }
                    if (isUploadingImage) disabled()
                    secondaryButtonStyle(colors, isUploadingImage)
                }) { Text(if (isUploadingImage) "Uploading..." else "Upload Image") }

                if (currentImageUrl != null) {
                    Button({
                        onClick {
                            scope.launch {
                                isUploadingImage = true
                                imageError = null
                                try {
                                    apiClient.removeAudiobookEpisodeImage(episode.audiobookIsbn, episode.id)
                                    currentImageUrl = null
                                    onEpisodeUpdated()
                                } catch (e: Exception) {
                                    imageError = e.message ?: "Failed to remove image"
                                } finally {
                                    isUploadingImage = false
                                }
                            }
                        }
                        if (isUploadingImage) disabled()
                        dangerButtonStyle(colors, isUploadingImage)
                    }) { Text("Remove Image") }
                }
            }
        }

        // ── Audio Section ──────────────────────────────────────────────────

        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.md)
                themeTransition()
            }
        }) {
            H4({ style { margin(0.px); fontSize(Typography.base); fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Audio File")
            }

            episode.audioUrl?.let {
                Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                    Text("Current audio: ✓ Uploaded")
                }
            }
            Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                val durationDisplay = episode.durationSeconds?.let { secs ->
                    val h = secs / 3600; val m = (secs % 3600) / 60; val s = secs % 60
                    "Duration: ${h}h ${m}m ${s}s"
                } ?: "Duration: --"
                Text(durationDisplay)
            }

            audioError?.let { ErrorBanner(it, colors) }
            if (audioSuccess) SuccessBanner("Audio replaced successfully", colors)

            Input(type = InputType.File) {
                attr("id", "episode-audio-upload-${episode.id}")
                attr("accept", ".mp3")
                style { property("display", "none") }
                onChange { event ->
                    val file = (event.target as? HTMLInputElement)?.files?.item(0)
                    if (file != null) {
                        pendingAudioFile = file
                        showAudioConfirm = true
                    }
                }
            }
            Button({
                onClick { kotlinx.browser.document.getElementById("episode-audio-upload-${episode.id}")?.asDynamic()?.click() }
                if (isReplacingAudio) disabled()
                secondaryButtonStyle(colors, isReplacingAudio)
            }) { Text(if (isReplacingAudio) "Replacing..." else "Replace Audio") }
        }

        // ── Translations Section ───────────────────────────────────────────

        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                themeTransition()
            }
        }) {
            EpisodeTranslationEditor(
                episode = episode,
                apiClient = apiClient,
                onTranslationSaved = onEpisodeUpdated
            )
        }

        // ── Delete / Restore Section ───────────────────────────────────────

        if (isAdmin) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fff7f7" else "#2a0a0a"))
                    border(1.px, LineStyle.Solid, Color(colors.danger))
                    borderRadius(BorderRadius.lg)
                    padding(Spacing.lg)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(Spacing.md)
                    themeTransition()
                }
            }) {
                H4({ style { margin(0.px); fontSize(Typography.base); fontWeight("600"); color(Color(colors.danger)) } }) {
                    Text("Danger Zone")
                }

                deleteError?.let { ErrorBanner(it, colors) }

                if (!isDeleted) {
                    Button({
                        onClick { showDeleteConfirm = true }
                        if (isDeleting) disabled()
                        dangerButtonStyle(colors, isDeleting)
                    }) { Text("Delete Chapter") }
                } else {
                    Button({
                        onClick {
                            scope.launch {
                                isRestoring = true
                                deleteError = null
                                try {
                                    apiClient.restoreAudiobookEpisode(episode.audiobookIsbn, episode.id)
                                    onEpisodeUpdated()
                                } catch (e: Exception) {
                                    deleteError = e.message ?: "Failed to restore"
                                } finally {
                                    isRestoring = false
                                }
                            }
                        }
                        if (isRestoring) disabled()
                        primaryButtonStyle(colors, isRestoring)
                    }) { Text(if (isRestoring) "Restoring..." else "Restore Chapter") }
                }
            }
        }
    }

    // Audio confirmation dialog
    if (showAudioConfirm) {
        ConfirmDialog(
            title = "Replace Audio File",
            message = "This will overwrite the existing audio file. This cannot be undone. Continue?",
            confirmLabel = "Replace",
            onConfirm = {
                showAudioConfirm = false
                val file = pendingAudioFile ?: return@ConfirmDialog
                scope.launch {
                    isReplacingAudio = true
                    audioError = null
                    try {
                        apiClient.replaceAudiobookEpisodeAudio(episode.audiobookIsbn, episode.id, file)
                        audioSuccess = true
                        onEpisodeUpdated()
                    } catch (e: Exception) {
                        audioError = e.message ?: "Audio replacement failed"
                    } finally {
                        isReplacingAudio = false
                        pendingAudioFile = null
                    }
                }
            },
            onCancel = {
                showAudioConfirm = false
                pendingAudioFile = null
            }
        )
    }

    // Delete confirmation dialog
    if (showDeleteConfirm) {
        ConfirmDialog(
            title = "Delete Chapter",
            message = "This chapter will be hidden from all users. Continue?",
            confirmLabel = "Delete",
            onConfirm = {
                showDeleteConfirm = false
                scope.launch {
                    isDeleting = true
                    deleteError = null
                    try {
                        apiClient.softDeleteAudiobookEpisode(episode.audiobookIsbn, episode.id)
                        onEpisodeUpdated()
                        onClose()
                    } catch (e: Exception) {
                        deleteError = e.message ?: "Failed to delete"
                    } finally {
                        isDeleting = false
                    }
                }
            },
            onCancel = { showDeleteConfirm = false }
        )
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    val colors = AppTheme.colors
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(Spacing.xs) } }) {
        Label(attrs = {
            style { fontSize(Typography.sm); fontWeight("600"); color(Color(colors.textMuted)) }
        }) { Text(label) }
        content()
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<org.w3c.dom.HTMLInputElement>.inputStyle(
    colors: AppColors
) {
    val scheme = colors
    style {
        width(100.percent)
        padding(Spacing.sm, Spacing.md)
        fontSize(Typography.base)
        border(1.px, LineStyle.Solid, Color(scheme.border))
        borderRadius(BorderRadius.md)
        backgroundColor(Color(scheme.surface))
        color(Color(scheme.text))
        property("box-sizing", "border-box")
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<org.w3c.dom.HTMLButtonElement>.primaryButtonStyle(
    colors: AppColors,
    disabled: Boolean = false
) {
    style {
        padding(Spacing.sm, Spacing.lg)
        fontSize(Typography.base)
        fontWeight("600")
        color(Color("#ffffff"))
        backgroundColor(Color(if (disabled) colors.textMuted else colors.primary))
        border(0.px)
        borderRadius(BorderRadius.md)
        cursor(if (disabled) "not-allowed" else "pointer")
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<org.w3c.dom.HTMLButtonElement>.secondaryButtonStyle(
    colors: AppColors,
    disabled: Boolean = false
) {
    style {
        padding(Spacing.sm, Spacing.lg)
        fontSize(Typography.base)
        fontWeight("600")
        color(Color(colors.text))
        backgroundColor(Color(colors.surfaceHover))
        border(1.px, LineStyle.Solid, Color(colors.border))
        borderRadius(BorderRadius.md)
        cursor(if (disabled) "not-allowed" else "pointer")
    }
}

private fun org.jetbrains.compose.web.attributes.AttrsScope<org.w3c.dom.HTMLButtonElement>.dangerButtonStyle(
    colors: AppColors,
    disabled: Boolean = false
) {
    style {
        padding(Spacing.sm, Spacing.lg)
        fontSize(Typography.base)
        fontWeight("600")
        color(Color("#ffffff"))
        backgroundColor(Color(if (disabled) colors.textMuted else colors.danger))
        border(0.px)
        borderRadius(BorderRadius.md)
        cursor(if (disabled) "not-allowed" else "pointer")
    }
}

@Composable
private fun ErrorBanner(message: String, colors: AppColors) {
    Div({
        style {
            fontSize(Typography.sm)
            color(Color(colors.danger))
            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
            padding(Spacing.sm)
            borderRadius(BorderRadius.sm)
        }
    }) { Text(message) }
}

@Composable
private fun SuccessBanner(message: String, colors: AppColors) {
    Div({
        style {
            fontSize(Typography.sm)
            color(Color("#16a34a"))
            backgroundColor(Color("#dcfce7"))
            padding(Spacing.sm)
            borderRadius(BorderRadius.sm)
        }
    }) { Text(message) }
}

@Composable
private fun ConfirmDialog(
    title: String,
    message: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style {
            position(Position.Fixed)
            top(0.px); left(0.px)
            width(100.percent); height(100.percent)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "2000")
        }
    }) {
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.xl)
                maxWidth(400.px)
                width(90.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.lg)
            }
        }) {
            H3({ style { margin(0.px); fontSize(Typography.lg); fontWeight("700"); color(Color(colors.text)) } }) {
                Text(title)
            }
            P({ style { margin(0.px); fontSize(Typography.base); color(Color(colors.text)) } }) {
                Text(message)
            }
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(Spacing.sm) } }) {
                Button({
                    onClick { onCancel() }
                    style {
                        padding(Spacing.sm, Spacing.lg); border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md); cursor("pointer"); backgroundColor(Color(colors.surface)); color(Color(colors.text))
                    }
                }) { Text("Cancel") }
                Button({
                    onClick { onConfirm() }
                    style {
                        padding(Spacing.sm, Spacing.lg); border(0.px); borderRadius(BorderRadius.md); cursor("pointer")
                        backgroundColor(Color(colors.danger)); color(Color("#ffffff")); fontWeight("600")
                    }
                }) { Text(confirmLabel) }
            }
        }
    }
}
